import {useEffect, useState} from "react";
import {Box, Button, MenuItem, Modal, Paper, TextField, Typography} from "@mui/material";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import {useAlert} from "../context/AlertContext";
import {updateCodeTask} from "../api/api";
import {CodeTaskRequest} from "../types";

type SupportedLang = "JAVA" | "GO" | "PYTHON" | "JAVA_CLASS" | "JAVA_METHOD" | "GO_STRUCT";

type EditableTask = {
    id: number;
    title: string;
    content: string;
    testCases: string;
    language: SupportedLang;
    orderNum: number;
    starterCode?: string | null;
};

interface Props {
    open: boolean;
    onClose: () => void;
    task: EditableTask | null;
    onUpdated: () => void;
}

const EditCodeTaskModal = ({open, onClose, task, onUpdated}: Props) => {
    const {showAlert} = useAlert();

    const [title, setTitle] = useState("");
    const [orderNum, setOrderNum] = useState<number>(1);
    const [language, setLanguage] = useState<SupportedLang>("JAVA");
    const [content, setContent] = useState("");
    const [testCases, setTestCases] = useState("");
    const [starterCode, setStarterCode] = useState("");

    const [saving, setSaving] = useState(false);
    const [testCasesError, setTestCasesError] = useState<string | null>(null);

    useEffect(() => {

        if (!open || !task) return;

        setTitle(task.title ?? "");
        setOrderNum(task.orderNum ?? 1);
        setLanguage(task.language ?? "JAVA");
        setContent(task.content ?? "");
        setTestCases(task.testCases ?? "");
        setTestCasesError(null);
        setStarterCode(task.starterCode ?? "");

    }, [open, task]);

    const ckeditorSx = (theme: any) => ({
        mb: 2,

        "& .ck.ck-editor": {
            width: "100%",
        },

        "& .ck.ck-editor__main > .ck-editor__editable": {
            minHeight: "220px",
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
            borderColor: `${theme.palette.divider} !important`,
        },

        "& .ck-content": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.paper : "#f5f5f5"
            } !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar .ck-button, & .ck.ck-toolbar .ck-button .ck-icon": {
            color: `${theme.palette.text.primary} !important`,
            fill: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-dropdown__panel": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    });

    const validateTestCases = (value: string): boolean => {
        const trimmed = value.trim();

        if (!trimmed) {
            setTestCasesError("Test cases are required (JSON array).");
            return false;
        }

        try {
            const parsed = JSON.parse(trimmed);

            if (!Array.isArray(parsed)) {
                setTestCasesError('Test cases must be a JSON array: [{"input":"...","expectedOutput":"..."}]');
                return false;
            }

            if (parsed.length === 0) {
                setTestCasesError("Test cases array cannot be empty.");
                return false;
            }

            for (let i = 0; i < parsed.length; i++) {
                const tc = parsed[i];
                const isObject = tc !== null && typeof tc === "object" && !Array.isArray(tc);
                if (!isObject) {
                    setTestCasesError(
                        `Invalid test case at index ${i}. Each item must be an object with "input" and "expectedOutput".`
                    );
                    return false;
                }

                if (typeof (tc as any).input !== "string") {
                    setTestCasesError(`Invalid test case at index ${i}: "input" must be a string.`);
                    return false;
                }

                if (typeof (tc as any).expectedOutput !== "string") {
                    setTestCasesError(`Invalid test case at index ${i}: "expectedOutput" must be a string.`);
                    return false;
                }

                if (((tc as any).expectedOutput as string).trim().length === 0) {
                    setTestCasesError(`Invalid test case at index ${i}: "expectedOutput" cannot be empty.`);
                    return false;
                }
            }

            setTestCasesError(null);
            return true;
        } catch {
            setTestCasesError("Invalid JSON. Please provide a valid JSON array of test cases.");
            return false;
        }
    };

    const prettifyJson = () => {
        try {
            const parsed = JSON.parse(testCases);
            setTestCases(JSON.stringify(parsed, null, 2));
            setTestCasesError(null);
        } catch {
            setTestCasesError("Cannot format JSON because it is invalid.");
        }
    };

    const submit = async () => {
        if (!task) return;

        const req: CodeTaskRequest = {
            title: title.trim(),
            content,
            testCases: testCases.trim(),
            language,
            orderNum: Number(orderNum),
            starterCode: starterCode.trim() ? starterCode : null,
        };

        if (!req.title) {
            showAlert("Title is required", "error");
            return;
        }
        if (!req.orderNum || req.orderNum < 1) {
            showAlert("Order must be >= 1", "error");
            return;
        }
        if (!req.content || !req.content.trim()) {
            showAlert("Task content is required", "error");
            return;
        }
        if (!validateTestCases(req.testCases)) {
            return;
        }

        try {
            setSaving(true);
            await updateCodeTask(task.id, req);
            showAlert("Task updated successfully", "success");
            onClose();
            onUpdated();
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to update task", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "min(1000px, 100vw)",
                    maxHeight: "100vh",
                    overflow: "auto",
                    outline: "none",
                }}
            >
                <Paper
                    elevation={1}
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 0,
                        p: 2,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Typography variant="h6">Edit Code Task</Typography>

                        <Button onClick={onClose} variant="outlined" sx={{textTransform: "none"}}>
                            Close
                        </Button>
                    </Box>

                    <Box sx={{px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2}}>
                        <Box sx={{display: "flex", gap: 2, flexWrap: "wrap"}}>
                            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)}
                                       fullWidth/>

                            <TextField
                                label="Order"
                                type="number"
                                value={orderNum}
                                onChange={(e) => setOrderNum(Number(e.target.value))}
                                sx={{width: 160}}
                            />

                            <TextField
                                select
                                label="Language"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as SupportedLang)}
                                sx={{width: 200}}
                            >
                                <MenuItem value="JAVA">JAVA</MenuItem>
                                <MenuItem value="GO">GO</MenuItem>
                                <MenuItem value="PYTHON">PYTHON</MenuItem>
                                <MenuItem value="JAVA_CLASS">JAVA_CLASS</MenuItem>
                                <MenuItem value="JAVA_METHOD">JAVA_METHOD</MenuItem>
                                <MenuItem value="GO_STRUCT">GO_STRUCT</MenuItem>
                            </TextField>
                        </Box>

                        <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                            Starter code (optional)
                        </Typography>

                        <TextField
                            value={starterCode}
                            onChange={(e) => setStarterCode(e.target.value)}
                            multiline
                            minRows={10}
                            fullWidth
                            placeholder={`// This code will be pre-filled for students if they have no attempt yet\n`}
                        />

                        <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                            Task content
                        </Typography>

                        <Box sx={ckeditorSx}>
                            <CKEditor
                                editor={ClassicEditor as any}
                                data={content}
                                onChange={(_event: any, editor: any) => {
                                    const html = editor.getData();
                                    setContent(html);
                                }}
                                config={{mediaEmbed: {previewsInData: true}}}
                            />
                        </Box>

                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                            <Typography variant="subtitle2" sx={{color: "text.secondary"}}>
                                Test cases (JSON format)
                            </Typography>

                            <Button variant="outlined" onClick={prettifyJson} sx={{textTransform: "none"}}
                                    disabled={saving}>
                                Prettify JSON
                            </Button>
                        </Box>

                        <TextField
                            label='Example: [{"input":"3 1 2 3","expectedOutput":"1"}]'
                            value={testCases}
                            onChange={(e) => {
                                const v = e.target.value;
                                setTestCases(v);
                                if (v.trim().length > 0) validateTestCases(v);
                                else setTestCasesError(null);
                            }}
                            onBlur={() => {
                                if (testCases.trim().length > 0) validateTestCases(testCases);
                            }}
                            multiline
                            minRows={10}
                            fullWidth
                            error={!!testCasesError}
                            helperText={testCasesError ?? 'Format: [{"input":"...","expectedOutput":"..."}] (both values must be strings)'}
                        />

                        <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1}}>
                            <Button onClick={onClose} variant="outlined" sx={{textTransform: "none"}}>
                                Cancel
                            </Button>
                            <Button onClick={submit} variant="contained" disabled={saving} sx={{textTransform: "none"}}>
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default EditCodeTaskModal;
